import { updateWorkflowTemplate } from '@commenda/api';
import {
  Alert,
  Button,
  FieldWrapper,
  Input,
  Label,
  Textarea,
  useToggle,
} from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useNotification } from '../../../hooks/useNotification';

const updateWorkflowTemplateSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  externalName: z.string().optional(),
});

type TUpdateWorkflowTemplateFormFields = z.infer<
  typeof updateWorkflowTemplateSchema
>;

export const UpdateWorkflowTemplateForm = ({
  workflowTemplateId,
  defaultData,
  onCancel,
  onSuccess,
  formContentClassName,
  formFooterClassName,
}: {
  workflowTemplateId: string;
  defaultData: {
    name: string;
    description?: string;
    externalName?: string;
    key?: string;
  };
  onCancel: () => void;
  onSuccess: () => void;
  formContentClassName?: string;
  formFooterClassName?: string;
}) => {
  const isSubmitting = useToggle(false);
  const { setNotification } = useNotification();
  const { register, handleSubmit } = useForm<TUpdateWorkflowTemplateFormFields>(
    {
      resolver: zodResolver(updateWorkflowTemplateSchema),
      defaultValues: defaultData,
    }
  );

  const onSubmit = async (data: TUpdateWorkflowTemplateFormFields) => {
    isSubmitting.on();
    const res = await updateWorkflowTemplate(workflowTemplateId, data);
    if (!res.ok) {
      setNotification({
        title: 'Error',
        message: 'Failed to update workflow.',
        show: true,
        valid: false,
      });
      isSubmitting.off();
      return;
    }
    setNotification({
      title: 'Success',
      message: 'Workflow updated successfully.',
      show: true,
      valid: true,
    });
    onSuccess();
    isSubmitting.off();
  };

  return (
    <div>
      <div className={cn('flex flex-col gap-4', formContentClassName)}>
        <FieldWrapper>
          <Label>Name</Label>
          <Input {...register('name')} />
        </FieldWrapper>
        <FieldWrapper>
          <Label>External Name</Label>
          <Alert variant="info" description="The external name serves as the customer-facing title of any service request created using this template." />
          <Input {...register('externalName')} />
        </FieldWrapper>
        <FieldWrapper>
          <Label>Key</Label>
          <Input disabled value={defaultData.key || ''} />
        </FieldWrapper>
        <FieldWrapper>
          <Label>Description</Label>
          <Textarea {...register('description')} />
        </FieldWrapper>
      </div>
      <div className={cn('flex gap-2 justify-end', formFooterClassName)}>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={isSubmitting.value} onClick={handleSubmit(onSubmit)}>
          {isSubmitting.value ? 'Updating...' : 'Update'}
        </Button>
      </div>
    </div>
  );
};
